import PropTypes from "prop-types";
import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { Spinner } from "@/components/ui/spinner";
import { FEATURE_FLAG_ACCESS_TYPE } from "constants/Constants";
import FeatureFlagService from "services/FeatureFlag";
import useAuthStore from "stores/useAuthStore";

const RuvixxFlagsContext = createContext({});

const evaluate = (flag, userId, roleId) => {
  if (!flag.enabled) return false;
  if (flag.access_type === FEATURE_FLAG_ACCESS_TYPE.EVERYONE) return true;
  return (
    (flag.users_access ?? []).includes(userId) ||
    (flag.roles_access ?? []).includes(roleId)
  );
};

/**
 * Fetches every live flag once and exposes a `{ [key]: boolean }` map
 * evaluated against the current user. Flags apply on full page reload only,
 * so the query never refetches and there is no invalidation after mutations.
 */
const RuvixxFlagsProvider = ({ children }) => {
  const userAuth = useAuthStore(state => state.userAuth);

  const { data: flags, isLoading } = useQuery({
    queryKey: ["feature-flags", "effective"],
    queryFn: () => FeatureFlagService.effective(),
    enabled: !!userAuth,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const value = useMemo(() => {
    if (!flags || !userAuth) return {};
    const userId = userAuth.id;
    const roleId = userAuth.role?.id;
    return flags.reduce((map, flag) => {
      map[flag.key] = evaluate(flag, userId, roleId);
      return map;
    }, {});
  }, [flags, userAuth]);

  // Flag-gated routes are omitted from the tree until the flags are known, so
  // rendering the app now would flash the Not Found page on any gated URL. Hold
  // the app on a spinner until the query settles (never for a signed-out user,
  // whose query stays disabled).
  if (isLoading) {
    return (
      <div className="tw:flex tw:h-screen tw:w-full tw:items-center tw:justify-center tw:bg-background">
        <Spinner className="tw:size-8" />
      </div>
    );
  }

  return (
    <RuvixxFlagsContext.Provider value={value}>
      {children}
    </RuvixxFlagsContext.Provider>
  );
};

RuvixxFlagsProvider.propTypes = {
  children: PropTypes.node,
};

const useRuvixxFlags = () => useContext(RuvixxFlagsContext);

export { RuvixxFlagsProvider, useRuvixxFlags };
